import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { FerryIngredientFilters } from '@apps/main/modules/recipe/ingredients/ferryIngredient';
import type { FlightIngredientFilters } from '@apps/main/modules/recipe/ingredients/flightIngredient';
import { useGlobalCurrencyStore } from '@apps/main/new-desktop/core/stores';
import { FerryFilterName } from '@apps/main/new-desktop/core/stores/filters/ferryFilter.model';
import { FlightFilterType } from '@apps/main/new-desktop/core/stores/filters/flightFilter.model';

import type {
  CarrierName,
  DecimalRangeType,
} from '@libs/trip-planner-interfaces';
import type { TagData } from '@libs/ui';
import {
  getHHMMFormatFromMinutes,
  getRoundedMinutesFromHours,
} from '@libs/utils';

const { t } = i18n.global;

const globalCurrencyStore = useGlobalCurrencyStore();

export function getFerryFilterPills(
  filters: FerryIngredientFilters,
  allCarriers: CarrierName[]
): TagData[] {
  const pillsFilter: TagData[] = [];
  if (
    filters.arrivalTimeFilters &&
    (filters.arrivalTimeFilters.from !== undefined ||
      filters.arrivalTimeFilters.to !== undefined)
  ) {
    pillsFilter.push({
      text: getTimeRangePillText(
        filters.arrivalTimeFilters.from,
        filters.arrivalTimeFilters.to,
        t('filter.ferryFilters.arrival')
      ),
      type: FerryFilterName.ArrivalTimeFilters,
    });
  }
  if (
    filters.departureTimeFilters &&
    (filters.departureTimeFilters.from !== undefined ||
      filters.departureTimeFilters.to !== undefined)
  ) {
    pillsFilter.push({
      text: getTimeRangePillText(
        filters.departureTimeFilters.from,
        filters.departureTimeFilters.to,
        t('filter.ferryFilters.departure')
      ),
      type: FerryFilterName.DepartureTimeFilters,
    });
  }
  if (filters.carrierFilters.length) {
    filters.carrierFilters.forEach((code) => {
      const carrier = allCarriers.find((carrier) => carrier.code === code);

      pillsFilter.push({
        text: carrier?.name || code,
        type: `${FerryFilterName.CarrierFilters}.${code}`,
      });
    });
  }
  return pillsFilter;
}

export function getFlightFilterPills(
  filters: FlightIngredientFilters,
  allCarriers: CarrierName[],
  currency: string
): TagData[] {
  const pillsFilter: TagData[] = [];
  if (
    filters.arrivalTimeFilters &&
    (filters.arrivalTimeFilters.from !== undefined ||
      filters.arrivalTimeFilters.to !== undefined)
  ) {
    pillsFilter.push({
      text: getTimeRangePillText(
        filters.arrivalTimeFilters.from,
        filters.arrivalTimeFilters.to,
        t('filter.flightFilters.arrival')
      ),
      type: FlightFilterType.ArrivalTimeFilters,
    });
  }
  if (filters.carrierFilters.length) {
    filters.carrierFilters.forEach((code) => {
      const carrier = allCarriers?.find((carrier) => carrier.code === code);

      pillsFilter.push({
        text: carrier?.name || code,
        type: `${FlightFilterType.CarrierFilters}.${code}`,
      });
    });
  }
  if (
    filters.departureTimeFilters &&
    (filters.departureTimeFilters.from !== undefined ||
      filters.departureTimeFilters.to !== undefined)
  ) {
    pillsFilter.push({
      text: getTimeRangePillText(
        filters.departureTimeFilters.from,
        filters.departureTimeFilters.to,
        t('filter.flightFilters.departure')
      ),
      type: FlightFilterType.DepartureTimeFilters,
    });
  }
  if (filters.maxDurationInHours) {
    pillsFilter.push({
      text: getDurationText(filters.maxDurationInHours),
      type: FlightFilterType.MaxDurationInHours,
    });
  }
  if (filters.maxNumberOfStops !== undefined) {
    pillsFilter.push({
      text: getNumberOfStepsText(filters.maxNumberOfStops),
      type: FlightFilterType.MaxNumberOfStops,
      value: filters.maxNumberOfStops,
    });
  }
  if (
    filters.priceFilters &&
    (filters.priceFilters.from !== undefined ||
      filters.priceFilters.to !== undefined)
  ) {
    pillsFilter.push({
      text: getPricePillText(filters.priceFilters, currency),
      type: FlightFilterType.PriceFilters,
    });
  }
  return pillsFilter;
}

function getTimeRangePillText(
  from: string | undefined,
  to: string | undefined,
  pillName: string
): string {
  const midnight = '0:00';
  const minuteBeforeMidnight = '23:59';

  return `${pillName}: ${from || midnight} - ${to || minuteBeforeMidnight}`;
}

function getPricePillText(price: DecimalRangeType, currency: string): string {
  const minPrice = globalCurrencyStore.formatMoney(
    Math.abs(Number(price.from || 0)),
    currency
  );
  const maxPrice = globalCurrencyStore.formatMoney(
    Math.abs(Number(price.to)),
    currency
  );

  return `${minPrice} - ${maxPrice}`;
}

function getDurationText(duaration: number): string {
  const minutes = getRoundedMinutesFromHours(duaration);
  return `${t('planner.total')}: ${getHHMMFormatFromMinutes(minutes)}`;
}

function getNumberOfStepsText(stops: number): string {
  return stops === 0
    ? t('planner.direct_flight_short')
    : t('planner.number_of_stops', [stops]);
}
